import React, { useState, useMemo } from "react";
import {
    Search, Plus, Pencil, Trash2, ChevronDown, ChevronRight, Tags,
    GripVertical, Eye, EyeOff, Building2, Sparkles, Languages, AlertTriangle,
    LayoutGrid, Rows3, MoreHorizontal, CheckSquare, Square, X,
    SlidersHorizontal, ArrowUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

/* ------------------------------------------------------------------ */
/*  Mock data — same dataset for all three options so comparison is   */
/*  apples-to-apples. No backend calls, no side effects.              */
/* ------------------------------------------------------------------ */

const MOCK_TREE = [
    {
        id: "l1-dental", name_en: "Dental Care", name_ar: "طب الأسنان",
        is_active: true, providers: 18, offers: 31, sort_order: 1,
        children: [
            { id: "l2-gen-dent", name_en: "General Dentistry", name_ar: "طب الأسنان العام", is_active: true, providers: 14, offers: 12, parent_ids: ["l1-dental"], tags_en: ["check-up", "cleaning", "filling"], tags_ar: ["كشف"], translation_complete: true },
            { id: "l2-cos-dent", name_en: "Cosmetic Dentistry", name_ar: "تجميل الأسنان", is_active: true, providers: 8, offers: 14, parent_ids: ["l1-dental"], tags_en: ["whitening", "veneers", "smile"], tags_ar: ["تبييض"], translation_complete: true },
            { id: "l2-ortho", name_en: "Orthodontics", name_ar: "تقويم الأسنان", is_active: true, providers: 6, offers: 5, parent_ids: ["l1-dental"], tags_en: ["braces", "invisalign"], tags_ar: [], translation_complete: false },
        ],
    },
    {
        id: "l1-derm", name_en: "Dermatology", name_ar: "الأمراض الجلدية",
        is_active: true, providers: 12, offers: 22, sort_order: 2,
        children: [
            { id: "l2-skin", name_en: "Skin Care", name_ar: "العناية بالبشرة", is_active: true, providers: 11, offers: 9, parent_ids: ["l1-derm"], tags_en: ["acne", "rosacea"], tags_ar: ["حب الشباب"], translation_complete: true },
            { id: "l2-laser", name_en: "Laser Hair Removal", name_ar: "إزالة الشعر بالليزر", is_active: true, providers: 7, offers: 11, parent_ids: ["l1-derm", "l1-cosmetic"], tags_en: ["laser", "permanent"], tags_ar: [], translation_complete: false },
            { id: "l2-injections", name_en: "Cosmetic Injections", name_ar: "حقن التجميل", is_active: false, providers: 4, offers: 2, parent_ids: ["l1-derm", "l1-cosmetic"], tags_en: ["botox", "filler"], tags_ar: ["بوتوكس"], translation_complete: true },
        ],
    },
    {
        id: "l1-womens", name_en: "Women's Health", name_ar: "صحة المرأة",
        is_active: true, providers: 9, offers: 15, sort_order: 3,
        children: [
            { id: "l2-obs", name_en: "Obstetrics", name_ar: "طب التوليد", is_active: true, providers: 7, offers: 5, parent_ids: ["l1-womens"], tags_en: ["pregnancy", "prenatal"], tags_ar: ["حمل"], translation_complete: true },
            { id: "l2-gyn", name_en: "Gynecology", name_ar: "طب النساء", is_active: true, providers: 9, offers: 8, parent_ids: ["l1-womens"], tags_en: ["check-up"], tags_ar: [], translation_complete: false },
            { id: "l2-fert", name_en: "Fertility", name_ar: "علاج الإخصاب", is_active: true, providers: 3, offers: 2, parent_ids: ["l1-womens"], tags_en: ["IVF", "fertility"], tags_ar: ["إخصاب"], translation_complete: true },
        ],
    },
    {
        id: "l1-vision", name_en: "Vision Care", name_ar: "العناية بالعيون",
        is_active: true, providers: 5, offers: 7, sort_order: 4,
        children: [
            { id: "l2-eye-surg", name_en: "Eye Surgery", name_ar: "جراحة العيون", is_active: true, providers: 4, offers: 5, parent_ids: ["l1-vision"], tags_en: ["LASIK"], tags_ar: ["ليزك"], translation_complete: true },
            { id: "l2-optom", name_en: "Optometry", name_ar: "فحص النظر", is_active: true, providers: 5, offers: 2, parent_ids: ["l1-vision"], tags_en: ["glasses"], tags_ar: [], translation_complete: false },
        ],
    },
    {
        id: "l1-cosmetic", name_en: "Cosmetic & Aesthetic", name_ar: "التجميل",
        is_active: false, providers: 0, offers: 0, sort_order: 5,
        children: [],
    },
    {
        id: "l1-lab", name_en: "Lab & Imaging", name_ar: "تحاليل وأشعة",
        is_active: true, providers: 6, offers: 9, sort_order: 6,
        children: [
            { id: "l2-blood", name_en: "Routine Blood Tests", name_ar: "تحاليل الدم", is_active: true, providers: 6, offers: 4, parent_ids: ["l1-lab"], tags_en: ["CBC", "lipid"], tags_ar: ["دم"], translation_complete: true },
            { id: "l2-hormone", name_en: "Hormone Panels", name_ar: "تحاليل الهرمونات", is_active: true, providers: 4, offers: 2, parent_ids: ["l1-lab"], tags_en: ["thyroid", "hormone"], tags_ar: [], translation_complete: false },
            { id: "l2-xray", name_en: "X-Ray & Ultrasound", name_ar: "أشعة وسونار", is_active: true, providers: 5, offers: 3, parent_ids: ["l1-lab"], tags_en: ["xray", "ultrasound"], tags_ar: ["سونار"], translation_complete: true },
        ],
    },
];

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                    */
/* ------------------------------------------------------------------ */

function StatusDot({ active, title }) {
    return <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${active ? "bg-success" : "bg-muted-foreground/40"}`} title={title} />;
}

function UsageChips({ providers, offers }) {
    return (
        <div className="inline-flex items-center gap-1 text-[11px]">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-secondary text-secondary-foreground">
                <Building2 className="h-3 w-3" />
                {providers}
            </span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-secondary text-secondary-foreground">
                <Sparkles className="h-3 w-3" />
                {offers}
            </span>
        </div>
    );
}

function TranslationBadge({ complete }) {
    if (complete) return null;
    return (
        <span title="Translation incomplete" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[10px] font-semibold">
            <Languages className="h-3 w-3" /> AR
        </span>
    );
}

function ParentChips({ ids, tree }) {
    if (!ids || ids.length <= 1) return null;
    const labels = ids.map((id) => tree.find((l) => l.id === id)?.name_en || id);
    return (
        <div className="inline-flex flex-wrap gap-1">
            {labels.map((l) => (
                <span key={l} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">{l}</span>
            ))}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Option A — Polish of existing layout                              */
/* ------------------------------------------------------------------ */

function OptionA() {
    const [expanded, setExpanded] = useState(new Set(["l1-dental", "l1-derm"]));
    const toggle = (id) => setExpanded((p) => {
        const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
    });

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="relative flex-1">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search categories…" className="ps-10" />
                </div>
                <select className="h-9 rounded-md border border-border bg-background px-3 text-sm">
                    <option>All statuses</option>
                    <option>Active</option>
                    <option>Inactive</option>
                </select>
                <Button variant="outline">Expand all</Button>
                <Button variant="outline">Collapse all</Button>
                <Button><Plus className="h-4 w-4 me-1" /> Add L1</Button>
            </div>

            <div className="space-y-3">
                {MOCK_TREE.map((l1) => (
                    <section key={l1.id} className="surface-card overflow-hidden">
                        <header className="flex items-center justify-between gap-3 px-5 py-4">
                            <button type="button" onClick={() => toggle(l1.id)} className="flex-1 flex items-center gap-3 text-start">
                                <StatusDot active={l1.is_active} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="text-base font-semibold truncate">{l1.name_en}</h3>
                                        <span className="text-xs text-muted-foreground">{l1.name_ar}</span>
                                        <Badge variant="outline" className="text-[10px]">{l1.children.length}</Badge>
                                        <UsageChips providers={l1.providers} offers={l1.offers} />
                                    </div>
                                </div>
                                {expanded.has(l1.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                            <div className="flex items-center gap-1">
                                <Button size="sm" variant="outline"><Plus className="h-4 w-4 me-1" /> L2</Button>
                                <Button size="icon" variant="ghost"><Pencil className="h-4 w-4" /></Button>
                                <Button size="icon" variant="ghost"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                            </div>
                        </header>
                        {expanded.has(l1.id) && l1.children.length > 0 && (
                            <div className="px-5 pb-5 pt-1 border-t border-border">
                                <div className="grid sm:grid-cols-2 gap-2 mt-3">
                                    {l1.children.map((l2) => (
                                        <div key={l2.id} className="rounded-xl border border-border bg-card px-4 py-3">
                                            <div className="flex items-start gap-3">
                                                <StatusDot active={l2.is_active} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <p className="text-sm font-semibold truncate">{l2.name_en}</p>
                                                        <span className="text-xs text-muted-foreground">{l2.name_ar}</span>
                                                        <TranslationBadge complete={l2.translation_complete} />
                                                    </div>
                                                    <div className="mt-1.5 flex items-center gap-2">
                                                        <UsageChips providers={l2.providers} offers={l2.offers} />
                                                        <ParentChips ids={l2.parent_ids} tree={MOCK_TREE} />
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap gap-1">
                                                        {(l2.tags_en || []).slice(0, 3).map((tag) => (
                                                            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary">{tag}</span>
                                                        ))}
                                                        {l2.tags_en?.length > 3 && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">+{l2.tags_en.length - 3}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-0.5">
                                                    <Button size="icon" variant="ghost" className="h-7 w-7"><Pencil className="h-3.5 w-3.5" /></Button>
                                                    <Button size="icon" variant="ghost" className="h-7 w-7"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                ))}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Option B — Tree-table with side panel                             */
/* ------------------------------------------------------------------ */

function OptionB() {
    const [expanded, setExpanded] = useState(new Set(["l1-dental", "l1-derm", "l1-womens"]));
    const [selected, setSelected] = useState(new Set(["l2-cos-dent"]));
    const [activeRow, setActiveRow] = useState("l2-laser");
    const toggle = (id) => setExpanded((p) => {
        const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
    const toggleSel = (id) => setSelected((p) => {
        const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
    });

    const flatRows = useMemo(() => {
        const rows = [];
        MOCK_TREE.forEach((l1) => {
            rows.push({ ...l1, level: 1 });
            if (expanded.has(l1.id)) {
                l1.children.forEach((l2) => rows.push({ ...l2, level: 2, parent_l1: l1.id }));
            }
        });
        return rows;
    }, [expanded]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
            <div className="space-y-3">
                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="Search categories…" className="ps-10" />
                    </div>
                    <Button variant="outline" size="sm">
                        <SlidersHorizontal className="h-4 w-4 me-2" />Filters
                        <span className="ms-2 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">1</span>
                    </Button>
                    <Button variant="outline" size="sm"><ArrowUpDown className="h-4 w-4 me-2" />Sort</Button>
                    <Button size="sm"><Plus className="h-4 w-4 me-1" />Add</Button>
                </div>

                {/* Bulk action bar (visible because 1 selected) */}
                {selected.size > 0 && (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 flex items-center justify-between">
                        <p className="text-sm font-medium">{selected.size} selected</p>
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline"><Eye className="h-3.5 w-3.5 me-1" />Activate</Button>
                            <Button size="sm" variant="outline"><EyeOff className="h-3.5 w-3.5 me-1" />Deactivate</Button>
                            <Button size="sm" variant="outline" className="text-destructive"><Trash2 className="h-3.5 w-3.5 me-1" />Delete</Button>
                            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                    </div>
                )}

                {/* Tree-table */}
                <div className="surface-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/30">
                            <tr>
                                <th className="w-8 ps-3"><Square className="h-4 w-4 text-muted-foreground" /></th>
                                <th className="text-start px-3 py-3 font-semibold">Name</th>
                                <th className="text-start px-3 py-3 font-semibold">Subcats</th>
                                <th className="text-start px-3 py-3 font-semibold">Providers</th>
                                <th className="text-start px-3 py-3 font-semibold">Offers</th>
                                <th className="text-start px-3 py-3 font-semibold">Tags</th>
                                <th className="text-start px-3 py-3 font-semibold">Status</th>
                                <th className="px-3 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {flatRows.map((r) => {
                                const isL1 = r.level === 1;
                                const isActive = activeRow === r.id;
                                return (
                                    <tr key={r.id}
                                        onClick={() => setActiveRow(r.id)}
                                        className={`border-t border-border cursor-pointer ${isActive ? "bg-primary/5" : "hover:bg-muted/20"}`}>
                                        <td className="ps-3 py-2.5">
                                            <button type="button" onClick={(e) => { e.stopPropagation(); toggleSel(r.id); }}>
                                                {selected.has(r.id)
                                                    ? <CheckSquare className="h-4 w-4 text-primary" />
                                                    : <Square className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
                                            </button>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <div className="flex items-center gap-2" style={{ paddingInlineStart: r.level === 2 ? "20px" : 0 }}>
                                                {isL1 ? (
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); toggle(r.id); }}
                                                            className="text-muted-foreground hover:text-foreground">
                                                        {expanded.has(r.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                    </button>
                                                ) : (
                                                    <span className="w-4 inline-block" />
                                                )}
                                                <StatusDot active={r.is_active} />
                                                <span className={isL1 ? "font-semibold" : ""}>{r.name_en}</span>
                                                <span className="text-xs text-muted-foreground">{r.name_ar}</span>
                                                {!isL1 && <TranslationBadge complete={r.translation_complete} />}
                                                {!isL1 && <ParentChips ids={r.parent_ids} tree={MOCK_TREE} />}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5 text-muted-foreground">{isL1 ? r.children.length : "—"}</td>
                                        <td className="px-3 py-2.5">{r.providers}</td>
                                        <td className="px-3 py-2.5">{r.offers}</td>
                                        <td className="px-3 py-2.5">
                                            {!isL1 && (
                                                <div className="inline-flex flex-wrap gap-1">
                                                    {(r.tags_en || []).slice(0, 2).map((t) => (
                                                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary">{t}</span>
                                                    ))}
                                                    {r.tags_en?.length > 2 && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">+{r.tags_en.length - 2}</span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {r.is_active
                                                ? <Badge variant="outline" className="border-success/40 text-success text-[10px]">Active</Badge>
                                                : <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                                        </td>
                                        <td className="px-3 py-2.5 text-end">
                                            <Button size="icon" variant="ghost" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Footer summary */}
                <p className="text-xs text-muted-foreground text-center">
                    6 categories · 14 subcategories · 1 inactive · 4 with incomplete translation
                </p>
            </div>

            {/* Side panel — preview of edit-in-place */}
            <aside className="surface-card p-5 h-fit sticky top-4">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Editing</p>
                        <h3 className="text-base font-semibold mt-1">Laser Hair Removal</h3>
                        <p className="text-xs text-muted-foreground">Subcategory · 2 parents</p>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7"><X className="h-4 w-4" /></Button>
                </div>

                <div className="space-y-3 text-sm">
                    <div>
                        <label className="text-xs font-semibold text-muted-foreground">Name (EN)</label>
                        <Input className="mt-1" defaultValue="Laser Hair Removal" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-muted-foreground">Name (AR)</label>
                        <Input className="mt-1" defaultValue="إزالة الشعر بالليزر" dir="rtl" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-muted-foreground">Parents</label>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-primary/10 text-primary font-medium">
                                Dermatology <X className="h-3 w-3 cursor-pointer" />
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-primary/10 text-primary font-medium">
                                Cosmetic & Aesthetic <X className="h-3 w-3 cursor-pointer" />
                            </span>
                            <button type="button" className="text-xs px-2 py-1 rounded border border-dashed border-border text-muted-foreground hover:text-foreground">
                                + Add parent
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-muted-foreground">Tags (EN)</label>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                            <span className="text-xs px-2 py-0.5 rounded bg-secondary">laser</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-secondary">permanent</span>
                            <button type="button" className="text-xs px-2 py-0.5 rounded border border-dashed border-border text-muted-foreground">+</button>
                        </div>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                        <span className="text-xs font-semibold">Active</span>
                        <span className="inline-flex items-center gap-1 text-[11px] text-success">
                            <span className="h-1.5 w-1.5 rounded-full bg-success" /> Visible to members
                        </span>
                    </div>
                    <div className="rounded-lg border border-warning/40 bg-warning/5 p-3 text-xs">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold text-foreground">Used by 7 providers, 11 offers</p>
                                <p className="text-muted-foreground mt-0.5">Deactivating will hide this from member search but keep existing links.</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                        <Button className="flex-1">Save</Button>
                        <Button variant="outline">Cancel</Button>
                    </div>
                </div>
            </aside>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Option C — Option B + drag-to-reorder + inline rename             */
/* ------------------------------------------------------------------ */

function OptionC() {
    const [expanded, setExpanded] = useState(new Set(["l1-dental", "l1-derm"]));
    const [renaming, setRenaming] = useState("l2-ortho");
    const toggle = (id) => setExpanded((p) => {
        const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
    });

    const flatRows = useMemo(() => {
        const rows = [];
        MOCK_TREE.forEach((l1) => {
            rows.push({ ...l1, level: 1 });
            if (expanded.has(l1.id)) {
                l1.children.forEach((l2) => rows.push({ ...l2, level: 2, parent_l1: l1.id }));
            }
        });
        return rows;
    }, [expanded]);

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search…" className="ps-10" />
                </div>
                <Button variant="outline" size="sm"><SlidersHorizontal className="h-4 w-4 me-2" />Filters</Button>
                <Button variant="outline" size="sm"><ArrowUpDown className="h-4 w-4 me-2" />Sort</Button>
                <Button size="sm"><Plus className="h-4 w-4 me-1" />Add</Button>
            </div>

            <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs text-primary inline-flex items-center gap-2">
                <GripVertical className="h-3.5 w-3.5" />
                Drag rows to reorder · Hold <kbd className="px-1 rounded bg-white border border-border text-[10px]">Alt</kbd> + drag to add a parent
            </div>

            <div className="surface-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/30">
                        <tr>
                            <th className="w-8" />
                            <th className="w-8" />
                            <th className="text-start px-3 py-3 font-semibold">Name</th>
                            <th className="text-start px-3 py-3 font-semibold">EN/AR</th>
                            <th className="text-start px-3 py-3 font-semibold">Providers</th>
                            <th className="text-start px-3 py-3 font-semibold">Offers</th>
                            <th className="text-start px-3 py-3 font-semibold">Tags</th>
                            <th className="text-start px-3 py-3 font-semibold">Updated</th>
                            <th className="px-3 py-3" />
                        </tr>
                    </thead>
                    <tbody>
                        {flatRows.map((r, i) => {
                            const isL1 = r.level === 1;
                            const isRenaming = renaming === r.id;
                            const beingDragged = i === 4; // visual hint only
                            return (
                                <tr key={r.id} className={`border-t border-border group ${beingDragged ? "opacity-50 bg-primary/5" : "hover:bg-muted/20"}`}>
                                    <td className="ps-2 py-2 text-center">
                                        <GripVertical className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground cursor-grab inline" />
                                    </td>
                                    <td className="py-2 text-center">
                                        <Square className="h-4 w-4 text-muted-foreground/40 inline" />
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-2" style={{ paddingInlineStart: r.level === 2 ? "20px" : 0 }}>
                                            {isL1 ? (
                                                <button type="button" onClick={() => toggle(r.id)} className="text-muted-foreground hover:text-foreground">
                                                    {expanded.has(r.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                </button>
                                            ) : (
                                                <span className="w-4 inline-block" />
                                            )}
                                            <StatusDot active={r.is_active} />
                                            {isRenaming ? (
                                                <Input autoFocus className="h-7 max-w-[220px]" defaultValue={r.name_en} onBlur={() => setRenaming(null)} />
                                            ) : (
                                                <span className={isL1 ? "font-semibold" : ""} onDoubleClick={() => setRenaming(r.id)}>
                                                    {r.name_en}
                                                </span>
                                            )}
                                            {!isL1 && <ParentChips ids={r.parent_ids} tree={MOCK_TREE} />}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        <div className="inline-flex items-center gap-1">
                                            <span className={r.name_en ? "text-foreground" : "text-muted-foreground italic"}>EN</span>
                                            <span className="text-muted-foreground">·</span>
                                            <span className={r.name_ar ? "text-foreground" : "text-amber-600 italic"}>AR</span>
                                            {!isL1 && !r.translation_complete && <TranslationBadge complete={false} />}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">{r.providers}</td>
                                    <td className="px-3 py-2">{r.offers}</td>
                                    <td className="px-3 py-2">
                                        {!isL1 && (
                                            <div className="inline-flex flex-wrap gap-1">
                                                {(r.tags_en || []).slice(0, 2).map((t) => (
                                                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary">{t}</span>
                                                ))}
                                                {r.tags_en?.length > 2 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">+{r.tags_en.length - 2}</span>}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">2 days ago</td>
                                    <td className="px-3 py-2 text-end">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex">
                                            <Button size="icon" variant="ghost" className="h-7 w-7"><Pencil className="h-3.5 w-3.5" /></Button>
                                            <Button size="icon" variant="ghost" className="h-7 w-7"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                <span>6 L1 · 14 L2 · 1 inactive · 4 untagged · 4 missing AR</span>
                <span>Tip: double-click a name to rename inline · Right-click for more</span>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Page wrapper                                                      */
/* ------------------------------------------------------------------ */

export default function CategoriesPreviewPage() {
    const [view, setView] = useState("B");

    return (
        <div className="max-w-7xl space-y-4">
            <div className="rounded-xl border border-amber-300/60 bg-amber-50 px-5 py-3 flex items-center justify-between" data-testid="preview-banner">
                <div className="flex items-center gap-3">
                    <Sparkles className="h-5 w-5 text-amber-600" />
                    <div>
                        <p className="text-sm font-semibold text-amber-900">Categories — design prototype</p>
                        <p className="text-xs text-amber-800/80">Visual-only mockup with hardcoded sample data. No backend calls. Switch options to compare.</p>
                    </div>
                </div>
                <a href="/admin/categories" className="text-xs text-amber-900 underline hover:no-underline">← Back to live page</a>
            </div>

            <header>
                <h1 className="text-3xl font-semibold tracking-tight">Categories — Prototype</h1>
                <p className="mt-1 text-sm text-muted-foreground">Compare three design directions before committing to one.</p>
            </header>

            <Tabs value={view} onValueChange={setView}>
                <TabsList className="w-full justify-start">
                    <TabsTrigger value="A" data-testid="opt-a"><Rows3 className="h-4 w-4 me-2" />A · Polish</TabsTrigger>
                    <TabsTrigger value="B" data-testid="opt-b"><LayoutGrid className="h-4 w-4 me-2" />B · Tree-table + side panel</TabsTrigger>
                    <TabsTrigger value="C" data-testid="opt-c"><GripVertical className="h-4 w-4 me-2" />C · Drag + inline rename</TabsTrigger>
                </TabsList>
                <TabsContent value="A" className="mt-4">
                    <OptionDescription
                        title="Option A — Polish the existing layout"
                        bullets={[
                            "Same expandable L1 → 2-col L2 grid",
                            "Always-visible action buttons (no more hover-only)",
                            "Status filter, usage chips per row, parent chips on poly-hierarchy L2s",
                            "Tag overflow collapses to +N",
                        ]}
                        effort="~2 hours"
                    />
                    <OptionA />
                </TabsContent>
                <TabsContent value="B" className="mt-4">
                    <OptionDescription
                        title="Option B — Tree-table with side panel"
                        bullets={[
                            "Single tree-table; rows indent for L2s, click chevron to expand",
                            "Right-side edit panel keeps you in context (no dialog spawn)",
                            "Bulk select with floating action bar",
                            "Translation completeness badge, usage counts, parent chips",
                        ]}
                        effort="~6 hours + small backend (usage endpoint)"
                    />
                    <OptionB />
                </TabsContent>
                <TabsContent value="C" className="mt-4">
                    <OptionDescription
                        title="Option C — Option B + drag-to-reorder + inline rename"
                        bullets={[
                            "Drag handle on every row to reorder siblings",
                            "Alt+drag re-parents L2s (poly-hierarchy)",
                            "Double-click a name to rename inline",
                            "Updated timestamp column, EN/AR completeness inline",
                        ]}
                        effort="~9 hours total (B + dnd-kit + inline edit polish)"
                    />
                    <OptionC />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function OptionDescription({ title, bullets, effort }) {
    return (
        <div className="surface-card p-4 mb-4">
            <h2 className="text-base font-semibold">{title}</h2>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground list-disc list-inside">
                {bullets.map((b) => <li key={b}>{b}</li>)}
            </ul>
            <p className="mt-2 text-xs text-muted-foreground"><strong className="text-foreground">Estimated effort:</strong> {effort}</p>
        </div>
    );
}
